import type { PostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import type { SmartApplyPartner, SmartApplyPartnerConfigKey } from './smartApplyPartners'
export type { SmartApplyPartner }

export type SmartApplyProductName = SmartApplyPartnerConfigKey | 'meta' | 'hootsuite'

export type SmartApplyProductEntry = {
  name: SmartApplyProductName
  displayNameKey: string
  descriptionKey: string
  logo: string
  isOptional: boolean
}

export type SmartApplyPartnerEntry = {
  featureFlag?: PostHogFeatureFlag
  requiresCountryCheck?: boolean
  logo: string
  modalImage: string
  externalApplyUrl?: string
  i18n: {
    card: { title: string; description: string }
    modal: { title: string; description: string; disclaimer: string }
  }
  products: SmartApplyProductEntry[]
}

export const SMART_APPLY_PARTNER_CONFIG: Record<SmartApplyPartner, SmartApplyPartnerEntry> = {
  adobe: {
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeAcrobat.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/adobe/adobe-apply-modal.webp'),
    i18n: {
      card: { title: 'typography.smartApply.adobe.title', description: 'typography.smartApply.adobe.description' },
      modal: {
        title: 'typography.productOffersDetail.adobeAcrobatPro.modal.title',
        description: 'typography.productOffersDetail.adobeAcrobatPro.modal.description',
        disclaimer: 'typography.productOffersDetail.adobeAcrobatPro.modal.disclaimer'
      }
    },
    products: [
      {
        name: 'adobeAcrobatPro',
        displayNameKey: 'typography.productOffersDetail.adobeAcrobatPro.modal.productName',
        descriptionKey: 'typography.productOffersDetail.adobeAcrobatPro.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeAcrobat.png'),
        isOptional: true
      },
      {
        name: 'adobeExpress',
        displayNameKey: 'typography.productOffersDetail.adobeExpress.modal.productName',
        descriptionKey: 'typography.productOffersDetail.adobeExpress.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png'),
        isOptional: true
      }
    ]
  },
  adobeExpress: {
    featureFlag: POSTHOG_FEATURE_FLAGS.CP_ADOBE_EXPRESS_SECOND_SMART_APPLY,
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/adobe/adobe-apply-modal.webp'),
    i18n: {
      card: {
        title: 'typography.smartApply.adobeExpress.title',
        description: 'typography.smartApply.adobeExpress.description'
      },
      modal: {
        title: 'typography.productOffersDetail.adobeExpress.modal.title',
        description: 'typography.productOffersDetail.adobeExpress.modal.description',
        disclaimer: 'typography.productOffersDetail.adobeExpress.modal.disclaimer'
      }
    },
    products: [
      {
        name: 'adobeExpress',
        displayNameKey: 'typography.productOffersDetail.adobeExpress.modal.productName',
        descriptionKey: 'typography.productOffersDetail.adobeExpress.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png'),
        isOptional: false
      }
    ]
  },
  openai: {
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Openai.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/open-ai/open-ai-apply-modal.webp'),
    i18n: {
      card: { title: 'typography.smartApply.openai.title', description: 'typography.smartApply.openai.description' },
      modal: {
        title: 'typography.productOffersDetail.openAi.modal.title',
        description: 'typography.productOffersDetail.openAi.modal.description',
        disclaimer: 'typography.productOffersDetail.openAi.modal.disclaimer'
      }
    },
    products: [
      {
        name: 'openai',
        displayNameKey: 'typography.productOffersDetail.openAi.modal.productName',
        descriptionKey: 'typography.productOffersDetail.openAi.modal.descriptionAllInOne',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Openai.png'),
        isOptional: false
      }
    ]
  },
  zoom: {
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Zoom.webp'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/zoom/zoom-apply-modal.webp'),
    i18n: {
      card: { title: 'typography.smartApply.zoom.title', description: 'typography.smartApply.zoom.description' },
      modal: {
        title: 'typography.productOffersDetail.zoom.modal.title',
        description: 'typography.productOffersDetail.zoom.modal.description',
        disclaimer: 'typography.productOffersDetail.zoom.modal.disclaimer'
      }
    },
    products: [
      {
        name: 'zoom',
        displayNameKey: 'typography.productOffersDetail.zoom.modal.productName',
        descriptionKey: 'typography.productOffersDetail.zoom.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Zoom.webp'),
        isOptional: false
      }
    ]
  },
  claude: {
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Claude.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/claude/claude-apply-modal.webp'),
    i18n: {
      card: { title: 'typography.smartApply.claude.title', description: 'typography.smartApply.claude.description' },
      modal: {
        title: 'typography.productOffersDetail.claude.modal.title',
        description: 'typography.productOffersDetail.claude.modal.description',
        disclaimer: 'typography.productOffersDetail.claude.modal.disclaimer'
      }
    },
    products: [
      {
        name: 'claude',
        displayNameKey: 'typography.productOffersDetail.claude.modal.productName',
        descriptionKey: 'typography.productOffersDetail.claude.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Claude.png'),
        isOptional: false
      }
    ]
  },
  meta: {
    featureFlag: POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY,
    requiresCountryCheck: true,
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/meta.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/meta/meta-apply-modal.webp'),
    externalApplyUrl: 'https://www.facebook.com/donate/signup/?source=goodstack_tile',
    i18n: {
      card: { title: 'typography.smartApply.meta.title', description: 'typography.smartApply.meta.description' },
      modal: {
        title: 'typography.productOffersDetail.meta.modal.title',
        description: 'typography.productOffersDetail.meta.modal.description',
        disclaimer: ''
      }
    },
    products: [
      {
        name: 'meta',
        displayNameKey: 'typography.productOffersDetail.meta.modal.productName',
        descriptionKey: 'typography.productOffersDetail.meta.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/meta.png'),
        isOptional: false
      }
    ]
  },
  hootsuite: {
    logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Hootsuite.png'),
    modalImage: require('@percent/cause-dashboard/common/assets/images/partner-offers/enablement-page/hootsuite/hootsuite-apply-modal.webp'),
    externalApplyUrl:
      'https://www.hootsuite.com/pages/landing/non-profit-discount-application-percent?utm_campaign=hootgiving&utm_medium=content_syndication&utm_source=sponsored&utm_partner=goodstack&utm_vertical=non-profit&utm_content=may_homepage_tile',
    i18n: {
      card: {
        title: 'typography.smartApply.hootsuite.title',
        description: 'typography.smartApply.hootsuite.description'
      },
      modal: {
        title: 'typography.productOffersDetail.hootsuite.modal.title',
        description: 'typography.productOffersDetail.hootsuite.modal.description',
        disclaimer: ''
      }
    },
    products: [
      {
        name: 'hootsuite',
        displayNameKey: 'typography.productOffersDetail.hootsuite.modal.productName',
        descriptionKey: 'typography.productOffersDetail.hootsuite.modal.productDescription',
        logo: require('@percent/cause-dashboard/common/assets/images/partner-offers/Hootsuite.png'),
        isOptional: false
      }
    ]
  }
}

export const ADOBE_SUB_PRODUCT_LOGOS = {
  adobeAcrobat: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeAcrobat.png'),
  adobeExpress: require('@percent/cause-dashboard/common/assets/images/partner-offers/AdobeExpress.png')
} as const
